import React, { useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import cx from 'classnames';

import styles from './QuotesSlider.module.scss';

import FadeIn from 'src/components/FadeIn';

type Quote = {
  quote: string;
  member: string;
};

export type Props = {
  className?: string;
  hasLargeQuote?: boolean;
  quotes: Quote[];
};

const QuotesSlider: React.FC<Props> = ({ className, hasLargeQuote = false, quotes }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      loop: true,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
    },
    [
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          timeout = setTimeout(() => {
            slider.next();
          }, 8000);
        }
        slider.on('created', () => {
          nextTimeout();
        });
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      },
    ]
  );

  const handleClick = (index) => {
    instanceRef.current?.moveToIdx(index);
  };

  const containerClass = hasLargeQuote ? styles.quotesSlider__containerLargeQuote : styles.quotesSlider__container;
  const quoteClass = hasLargeQuote ? 'text-display_2 font-style-italic' : 'font-size-large-paragraph';

  return (
    <div className={cx(styles.quotesSlider, className)} data-cy="quotes-slider" ref={sliderRef}>
      <div className="keen-slider">
        {quotes.map((quote, index) => (
          <div
            key={`quotes-slide-${quote.member}`}
            aria-describedby={`slide-${index}`}
            aria-hidden={currentSlide !== index}
            role="option"
            className="keen-slider__slide"
            data-cy={`quotes-slide-${quote.member}`}
          >
            <FadeIn duration={0.4} up>
              <div className="container">
                <div className={containerClass}>
                  <svg className={styles.quotesSlider__icon} aria-hidden="true">
                    <use xlinkHref="#quote" />
                  </svg>
                  <p className={`${styles.quotesSlider__quote} ${quoteClass} color-white`}>{quote.quote}</p>
                  <div className={styles.quotesSlider__divider}></div>
                  <p className={`${styles.quotesSlider__member} color-white`}>{quote.member}</p>
                </div>
              </div>
            </FadeIn>
          </div>
        ))}
      </div>
      {loaded && (
        <ul className={styles.quotesSlider__dots}>
          {quotes.map((quote, index) => {
            const isCurrentSlide = currentSlide === index;
            return (
              <li
                key={`slider-dot-${index}`}
                aria-hidden={!isCurrentSlide}
                role="presentation"
                id={`slide-${index}`}
                data-cy={`quotes-dot-${quote.member}`}
              >
                <button
                  className={`${styles.quotesSlider__dot} ${isCurrentSlide && styles.quotesSlider__dotActive}`}
                  onClick={() => handleClick(index)}
                >
                  {index + 1}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export { QuotesSlider };
